import { z } from "zod";
import type { Address, Hex } from "viem";

const address = z.string().regex(/^0x[0-9a-f]{40}$/i).transform(value => value as Address);
const hash = z.string().regex(/^0x[0-9a-f]{64}$/i).transform(value => value as Hex);
const block = z.number().int().nonnegative().safe();
export const v4PoolKeySchema = z.object({
  currency0: address, currency1: address, fee: z.number().int().min(0).max(0xffffff),
  tickSpacing: z.number().int().min(1).max(0x7fffff), hooks: address,
});
/** Nullable fields represent incomplete configuration, never a legacy fallback. */
export const merkleV4TradeSchema = z.object({
  schemaVersion: z.literal(1), venue: z.literal("MERKLE_V4"), chainId: z.literal(99901),
  configurationStatus: z.enum(["COMPLETE", "INCOMPLETE"]), missingConfiguration: z.array(z.string()),
  tokenAddress: address.nullable(), tokenDecimals: z.literal(18),
  nativeCurrency: z.object({ address, symbol: z.literal("HYPE"), decimals: z.literal(18) }),
  poolAddress: z.null(), poolManagerAddress: address.nullable(), poolId: hash.nullable(),
  poolCreatedBlock: block.nullable(), poolKey: v4PoolKeySchema.nullable(),
  universalRouterAddress: address.nullable(), routerRuntimeCodeHash: hash.nullable(),
  feeHookAddress: address.nullable(), rewardVaultAddress: address.nullable(), permit2Address: address.nullable(),
  stateViewAddress: address.nullable(), positionManagerAddress: address.nullable(), lpLockAddress: address.nullable(),
  fees: z.object({ lpFee: z.literal(0), hookFeeBps: z.literal(300), feeCurrency: address }),
  execution: z.object({ swapType: z.literal("EXACT_INPUT_SINGLE"), command: z.literal("0x10"),
    actions: z.literal("0x060c0f"), swapStructVersion: z.literal("POOL_KEY_DIRECTION_AMOUNT_MIN_OUT_HOOK_DATA"),
    hookData: z.literal("0x"), buyZeroForOne: z.literal(true), sellZeroForOne: z.literal(false) }),
  quote: z.object({ method: z.literal("UNIVERSAL_ROUTER_REVERT_PROBE"), quoterAddress: z.null(),
    dedicatedQuoterStatus: z.literal("NOT_CONFIGURED"), outputIncludesHookFee: z.literal(true),
    minimumReceivedBase: z.literal("amountOut"), slippageDenominator: z.literal(10000),
    requiresFullInput: z.literal(true), simulateBeforeSubmit: z.literal(true) }),
  approval: z.object({ buy: z.object({ required: z.literal(false), payment: z.literal("NATIVE_VALUE") }),
    sell: z.object({ method: z.literal("ERC20_THEN_PERMIT2"), tokenAddress: address.nullable(),
      erc20Spender: address.nullable(), permit2Spender: address.nullable(), checkAllowanceExpiration: z.literal(true) }) }),
});

/** Serializable public evidence retained with a transaction for recovery after config changes. */
export const savedMerkleV4ConfigSchema = z.object({
  chainId: z.literal(99901), environment: z.literal("production"),
  deploymentBlock: z.string().regex(/^(0|[1-9][0-9]*)$/).max(16),
  token: address, poolManager: address, pool: address, poolId: hash, poolKey: v4PoolKeySchema,
  hook: address, router: address, vault: address, stateView: address, permit2: address,
  routerHash: hash, permit2Hash: hash,
});
